
import React from 'react';
import { ThumbsUp, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import UserAvatar from '@/components/Profile/UserAvatar';

interface DatabaseComment {
  id: string;
  created_at: string;
  content: string;
  story_id: string;
  user_id: string;
  author_name: string;
  author_avatar?: string;
  likes: number;
}

interface CommentItemProps {
  comment: DatabaseComment;
  currentUser: any | null;
  onLike: (commentId: string) => void;
  userLikes: string[];
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  currentUser,
  onLike,
  userLikes
}) => {
  const isLiked = userLikes.includes(comment.id);
  const { resolvedTheme } = useTheme();
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const metaTextColor = resolvedTheme === 'dark' ? 'text-gray-300' : 'text-gray-500';
  
  return (
    <div className="pb-6 border-b border-gray-800">
      <div className="flex items-start">
        <UserAvatar
          src={comment.author_avatar}
          fallback={comment.author_name.charAt(0)}
          size="sm"
          className="mr-4"
        />
        <div className="flex-1">
          <div className="flex justify-between items-center mb-2">
            <h4 className={cn("font-medium", textColor)}>{comment.author_name}</h4>
            <span className={cn("text-sm", metaTextColor)}>
              {new Date(comment.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
          </div>
          <p className={cn("mb-3", textColor)}>{comment.content}</p>
          <div className={cn("flex items-center text-sm", metaTextColor)}>
            <button 
              className={cn("flex items-center mr-6 hover:text-gray-300", isLiked ? textColor : '')} 
              onClick={() => onLike(comment.id)}
              disabled={!currentUser}
            >
              <ThumbsUp size={14} className="mr-1" />
              {isLiked ? 'Liked' : 'Like'} {comment.likes > 0 && `(${comment.likes})`}
            </button>
            <button className="flex items-center hover:text-gray-300" disabled={!currentUser}>
              <MessageCircle size={14} className="mr-1" />
              Reply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
